import React from 'react';
import { 
  ArrowUpCircle,
  Zap,
  Settings2,
  BarChart3,
  RefreshCw,
  Gauge,
  Wrench,
  ArrowRightCircle,
  Shield,
  Phone
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img from "../../Images/upg.jpg";
import img1 from "../../Images/upg1.jpeg";

const SystemUpgradesPage = () => {
  const services = [
    {
      title: "Performance Enhancement",
      description: "Modernize your existing systems with cutting-edge technology for improved efficiency and productivity.",
      icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
    },
    {
      title: "System Integration",
      description: "Seamless integration of new components with existing infrastructure for optimal performance.",
      icon: <Settings2 className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
    },
    {
      title: "Control System Updates",
      description: "Advanced control system upgrades for enhanced monitoring and automation capabilities.",
      icon: <Gauge className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
    }
  ];

  const upgradeBenefits = [
    {
      title: "Efficiency Gain",
      description: "Increased operational efficiency through modern technology",
      icon: <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />,
      stat: "Up to 25%"
    },
    {
      title: "Downtime Reduction",
      description: "Minimized system downtime through predictive maintenance",
      icon: <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />,
      stat: "30% Less"
    },
    {
      title: "ROI Timeline",
      description: "Quick return on upgrade investments",
      icon: <ArrowUpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />,
      stat: "8-12 Months"
    }
  ];

  const upgradeProcess = [
    {
      title: "System Audit",
      description: "Comprehensive evaluation of existing infrastructure"
    },
    {
      title: "Upgrade Planning",
      description: "Detailed modernization strategy and timeline"
    },
    {
      title: "Implementation",
      description: "Systematic upgrade execution with minimal disruption"
    },
    {
      title: "Optimization",
      description: "Fine-tuning and performance verification"
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Industrial System Upgrades & Modernization",
    "description": "Comprehensive industrial system upgrade services including performance enhancement, control system updates, and system integration",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "url": "https://www.gorthermenergy.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    }
  };

  return (
    <>
      <Helmet>
        <title>Industrial System Upgrades & Modernization | Control Systems & Performance Enhancement | Gortherm Energy</title>
        <meta name="description" content="Professional industrial system upgrade and modernization services. Performance enhancement, control system updates, system integration, and automation upgrades. Increase efficiency up to 25% with expert modernization solutions." />
        <meta name="keywords" content="system upgrades, industrial modernization, control system upgrade, performance enhancement, system integration, automation upgrade, boiler upgrade, SCADA upgrade, industrial retrofit, system optimization Nepal" />
        <meta property="og:title" content="Industrial System Upgrades & Modernization | Gortherm Energy" />
        <meta property="og:description" content="Modernize your industrial systems with cutting-edge technology solutions for enhanced performance and efficiency." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/system-upgrades" />
        <link rel="canonical" href="https://www.gorthermenergy.com/system-upgrades" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <main className="w-full">
        <header className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-r from-slate-900 to-blue-900 overflow-hidden pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img src={img1} alt="Industrial system upgrades and modernization services" className="w-full h-full object-cover opacity-20" loading="eager" width="1920" height="400" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Industrial System Upgrades</h1>
              <p className="text-base sm:text-xl lg:text-2xl mb-6 sm:mb-8">Modernize your industrial systems with cutting-edge technology solutions for enhanced performance and efficiency.</p>
              <a href="/contact"><button className="bg-purple-500 hover:bg-purple-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg flex items-center gap-2 transition-all text-sm sm:text-base font-semibold w-full sm:w-auto">Explore Solutions<ArrowRightCircle className="w-4 h-4 sm:w-5 sm:h-5" /></button></a>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <section className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Upgrade Services</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto px-4">Comprehensive system upgrade solutions designed to enhance performance, reliability, and operational efficiency.</p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {services.map((service, index) => (
              <article key={index} className="p-4 sm:p-6 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
              </article>
            ))}
          </div>

          <section className="bg-purple-50 rounded-xl p-6 sm:p-8 mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Upgrade Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {upgradeBenefits.map((benefit, index) => (
                <div key={index} className="text-center p-4 sm:p-6 bg-white rounded-lg shadow-sm">
                  <div className="flex justify-center mb-3 sm:mb-4">{benefit.icon}</div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">{benefit.description}</p>
                  <div className="text-xl sm:text-2xl font-bold text-purple-600">{benefit.stat}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center">Upgrade Process</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {upgradeProcess.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <span className="text-purple-600 font-bold text-sm sm:text-base">{index + 1}</span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">{step.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 text-center">{step.description}</p>
                  </div>
                  {index < upgradeProcess.length - 1 && <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-[2px] bg-purple-100"></div>}
                </div>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
            <div className="relative h-64 sm:h-80 md:h-96 order-2 md:order-1">
              <img src={img} alt="Industrial system upgrade process" className="rounded-lg shadow-xl object-cover h-full w-full" loading="lazy" width="600" height="400" />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Technical Capabilities</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0"><Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" /></div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Modernization Expertise</h3>
                    <p className="text-sm sm:text-base text-gray-600">Skilled engineering team with extensive experience in system upgrades and modernization projects.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0"><Shield className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" /></div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Quality Assurance</h3>
                    <p className="text-sm sm:text-base text-gray-600">Rigorous testing and validation procedures ensuring optimal system performance.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <aside className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Ready to Upgrade Your Systems?</h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">Consult with our system upgrade experts to modernize your operations and maximize efficiency.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact"><button className="bg-purple-600 hover:bg-purple-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base transition-all w-full sm:w-auto">Request System Audit</button></a>
              <a href="tel:+9779802781400"><button className="bg-white text-purple-600 border-2 border-purple-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg flex items-center gap-2 justify-center font-semibold text-sm sm:text-base transition-all w-full sm:w-auto"><Phone className="h-5 w-5" />Call Our Experts</button></a>
            </div>
           
          </aside>
        </div>
      </main>
    </>
  );
};

export default SystemUpgradesPage;